import { GetInferredFromRaw } from '@thinknimble/tn-models'
import { z } from 'zod'
import { baseModelShape, selectOptionsShape } from '../base-model'
import { organizationShape, userShape } from '../user'
import { receiptShape } from '../receipts'
import { reportShape } from '../expense-reports'
import { paymentAccountShape } from '../payment-accounts'
import { vendorShape } from '../vendors/models'

// Native enums
export const transactionTypeEnum = {
  EXPENSE: 'expense',
  INCOME: 'income',
  TRANSFER: 'transfer',
  OWNER_INVESTMENT: 'owner_investment',
  OWNER_DISTRIBUTION: 'owner_distribution',
  ASSET_PURCHASE: 'asset_purchase',
} as const

export type TransactionTypeValues = (typeof transactionTypeEnum)[keyof typeof transactionTypeEnum]

export const transactionTypeLabelMap = {
  [transactionTypeEnum.EXPENSE]: 'Business Expense',
  [transactionTypeEnum.INCOME]: 'Income/Revenue',
  [transactionTypeEnum.TRANSFER]: 'Transfer Between Accounts',
  [transactionTypeEnum.OWNER_INVESTMENT]: 'Owner Investment/Contribution',
  [transactionTypeEnum.OWNER_DISTRIBUTION]: 'Owner Distribution/Draw',
}

export const expenseGroupCreateShape = {
  name: z.string(),
  amount: z.number().or(z.string()),
  amountCurrency: z.string(),
  notes: z.string().nullable().optional(),
  reimbursementNotes: z.string().nullable().optional(),
  isReimbursable: z.boolean(),
  isReimbursed: z.boolean(),
  expenseDate: z.string(),
  organization: z.string().nullable(),
  receipts: z.array(z.string()).optional(),
  reimburseTo: z.string().nullable(),
  providedId: z.string().nullable().optional(),
  expenseCategory: z.string().nullable().optional(),
  cashFlowType: z.string().nullable().optional(),
  reports: z.array(z.string()).optional(),
  paymentAccount: z.string().nullable().optional(),
  vendor: z.string().nullable().optional(),
  // Reconciliation and Tax fields
  needsReview: z.boolean().optional(),
  isReconciled: z.boolean().optional(),
  taxYear: z.number().nullable().optional(),
  transactionType: z.enum(transactionTypeEnum).nullable().optional(),
  personalUsePercentage: z.number().nullable().optional(),
  milesDriven: z.number().nullable().optional(),
  // Sending a value marks the converted amount as manual; sending null resumes automatic FX conversion.
  defaultConvertedAmount: z.number().or(z.string()).nullable().optional(),
  // Id of the AI generation (ReceiptExtraction) this expense was created from.
  receiptExtraction: z.string().nullable().optional(),
}

// The transaction type ref carries a null value when no type is set.
const transactionTypeRefShape = {
  label: z.string(),
  value: z.string().nullable(),
}

export const expenseGroupShape = {
  ...baseModelShape,
  ...expenseGroupCreateShape,
  // Required on create, but stored rows may have no date.
  expenseDate: z.string().nullable(),
  organizationRef: z.object(organizationShape).nullable(),
  receiptsRef: z.array(z.object(receiptShape)).optional(),
  reimburseToRef: z.object(userShape).nullable().optional(),
  reportsRef: z.array(z.object(reportShape)).optional(),
  paymentAccountRef: z.object(paymentAccountShape).nullable().optional(),
  vendorRef: z.object(vendorShape).nullable().optional(),
  categoryRef: z.object(selectOptionsShape).nullable().optional(),
  cashFlowTypeRef: z.object(selectOptionsShape).nullable().optional(),
  transactionTypeRef: z.object(transactionTypeRefShape).nullable().optional(),
  similarityScore: z.number().nullable().optional(),
  hasCustomReceipt: z.boolean().optional(),
  // Converted amount in org's default currency
  defaultConvertedAmountCurrency: z.string().nullable().optional(),
  convertedAmountIsManual: z.boolean().optional(),
}

export const expenseFilterShape = {
  search: z.string(),
  expenseDate: z.string(),
  expenseDate__gte: z.string(),
  expenseDate__lte: z.string(),
  expenseYear: z.string(),
  created: z.string(),
  organization: z.array(z.string()),
  cashFlowType: z.string().array(),
  category: z.string().array(),
  expenseSearch: z.string().array(),
  reports: z.string().array(),
  paymentAccount: z.string().array(),
  transactionType: z.string().array(),
  isReimbursable: z.boolean(),
  isReimbursed: z.boolean(),
  hasReceipts: z.boolean(),
  hasReports: z.boolean(),
  needsReview: z.boolean(),
  amount: z.number(),
  amount__gte: z.number(),
  amount__lte: z.number(),
  isReconciled: z.boolean(),
  includeReimbursableOptions: z.boolean(),
  ordering: z.string(),
}

export const retencionTypeEnum = {
  PERCENTAGE: 'percentage',
  FIXED: 'fixed',
} as const

export type RetencionTypeValues = (typeof retencionTypeEnum)[keyof typeof retencionTypeEnum]

export type ExpenseGroup = GetInferredFromRaw<typeof expenseGroupShape>
export type ExpenseFilter = GetInferredFromRaw<typeof expenseFilterShape>
export type ExpenseReport = GetInferredFromRaw<typeof reportShape>
